import type { WorkspaceCardId } from './workspace-layout';

export type WorkspacePresetId = 'operations' | 'finance' | 'procurement';

export const WORKSPACE_PRESETS: readonly {
  id: WorkspacePresetId;
  label: string;
  cardIds: readonly WorkspaceCardId[];
  kpiIds: readonly string[];
}[] = [
  {
    id: 'operations',
    label: 'Operations',
    cardIds: [
      'radar',
      'collaboration',
      'recent-sales-orders',
      'purchase-orders-at-risk',
      'expected-arrivals',
      'inventory-health',
      'shipment-flow',
      'warehouse-task-aging',
      'invoice-inbox',
      'invoice-cash',
      'invoice-payments',
      'recent-purchase-orders',
      'warehouse-capacity',
    ],
    kpiIds: [
      'pending-orders',
      'low-stock',
      'held-shipments',
      'overdue-invoices',
      'revenue',
      'stockouts',
      'invoice-approvals',
      'unreconciled-payments',
      'shipments',
      'orders',
      'inventory',
      'available-units',
      'average-order-value',
    ],
  },
  {
    id: 'finance',
    label: 'Finance',
    cardIds: [
      'collaboration',
      'invoice-cash',
      'invoice-inbox',
      'invoice-payments',
      'purchase-orders-at-risk',
      'expected-arrivals',
      'recent-sales-orders',
    ],
    kpiIds: [
      'overdue-invoices',
      'invoice-approvals',
      'unreconciled-payments',
      'invoice-exceptions',
      'revenue',
      'average-order-value',
      'pending-orders',
      'orders',
    ],
  },
  {
    id: 'procurement',
    label: 'Procurement',
    cardIds: [
      'collaboration',
      'purchase-orders-at-risk',
      'expected-arrivals',
      'recent-purchase-orders',
      'inventory-health',
      'invoice-inbox',
      'shipment-flow',
    ],
    kpiIds: [
      'kpi:procurement_pipeline:open_rfq_count',
      'kpi:supplier_delay_risk:average_days_late',
      'kpi:backorder_exposure:backorder_count',
      'low-stock',
      'stockouts',
      'invoice-approvals',
      'kpi:purchase_order_pipeline:purchase_order_count',
      'available-units',
      'held-shipments',
    ],
  },
];

/** Only choose a role-specific starting point when the role or access is clear. */
export function inferWorkspacePreset(
  roles: readonly string[],
  availableCardIds: readonly WorkspaceCardId[]
): WorkspacePresetId {
  if (roles.some((role) => /finance|accountant|accounting|accounts[_ -]payable/i.test(role))) {
    return 'finance';
  }
  if (roles.some((role) => /procurement|purchasing|sourcing/i.test(role))) return 'procurement';
  const hasOperations = availableCardIds.some((id) =>
    ['radar', 'inventory-health', 'shipment-flow', 'recent-sales-orders'].includes(id)
  );
  if (!hasOperations && availableCardIds.includes('invoice-inbox')) return 'finance';
  if (!hasOperations && availableCardIds.includes('recent-purchase-orders')) return 'procurement';
  return 'operations';
}

export function workspacePresetSelection(
  presetId: WorkspacePresetId,
  availableCardIds: readonly WorkspaceCardId[],
  availableKpiIds: readonly string[]
): { cardIds: WorkspaceCardId[]; kpiIds: string[] } {
  const preset = WORKSPACE_PRESETS.find((item) => item.id === presetId) ?? WORKSPACE_PRESETS[0];
  const choose = <T extends string>(
    priorities: readonly T[],
    available: readonly T[],
    limit: number
  ) =>
    [...new Set([...priorities, ...available])]
      .filter((id) => available.includes(id))
      .slice(0, limit);
  const cardIds = choose(preset.cardIds, availableCardIds, 6);
  return {
    cardIds,
    // Operations and Finance reuse the page summaries; Procurement benefits
    // from sourcing and supplier measures when Analytics is available.
    kpiIds: choose(
      preset.kpiIds,
      availableKpiIds.filter((id) => presetId === 'procurement' || !id.startsWith('kpi:')),
      5
    ),
  };
}

/** Governed system charts complement the current-state KPI strip. */
export const WORKSPACE_DEFAULT_CHARTS = [
  {
    id: 'chart:system:visual:product-revenue-leaders' as WorkspaceCardId,
    title: 'Product Revenue Leaders',
    plugin: 'products',
    permissions: ['product:product:read', 'order:sales:read'],
  },
] as const;

/** Place supply timing and product performance beside the decisions they support. */
export function arrangeWorkspaceDefaultCards(
  presetId: WorkspacePresetId,
  cardIds: readonly WorkspaceCardId[],
  chartIds: readonly WorkspaceCardId[]
): WorkspaceCardId[] {
  const arrival: WorkspaceCardId = 'expected-arrivals';
  const products = WORKSPACE_DEFAULT_CHARTS[0].id;
  const selected = new Set([...cardIds, ...chartIds]);
  const order: readonly WorkspaceCardId[] =
    presetId === 'operations'
      ? [
          'radar',
          'collaboration',
          'recent-sales-orders',
          'purchase-orders-at-risk',
          arrival,
          'inventory-health',
          products,
          'shipment-flow',
        ]
      : presetId === 'finance'
        ? [
            'invoice-cash',
            'invoice-inbox',
            'invoice-payments',
            'collaboration',
            'purchase-orders-at-risk',
            'recent-sales-orders',
            arrival,
            'inventory-health',
            products,
          ]
        : [
            'purchase-orders-at-risk',
            'inventory-health',
            arrival,
            'recent-purchase-orders',
            'collaboration',
            'invoice-inbox',
            'shipment-flow',
            products,
          ];
  const arranged = [...new Set([...order, ...cardIds, ...chartIds])].filter((id) => selected.has(id));
  // Keep the standard cards together in matching rows, followed by the
  // full-width calendar. Existing saved layouts bypass these defaults.
  return [...arranged.filter((id) => id !== arrival), ...arranged.filter((id) => id === arrival)];
}
